import { createContext, type ReactNode, useContext, useMemo } from "react";

import type {
  AppContextValue,
  AppFeatureFlags,
  AppPlatform,
  AppScope,
  Notifier,
  WorkspaceAppScope,
} from "./types";

const NO_SCOPE: AppScope = { kind: "none" };
const NO_FLAGS: AppFeatureFlags = {
  featureFlags: undefined,
  isLoading: false,
  isFetching: false,
  error: undefined,
  refetch: () => undefined,
};
const SILENT: Notifier = {
  verbosity: "concise",
  success: () => undefined,
  info: () => undefined,
  error: () => undefined,
};

/**
 * Outside any app: no scope, no flags, nothing said. What a promise modal in
 * its own React root sees, as `useParams` outside the router gave nothing
 * before. It imports nothing of `state/`, so an app that gives its own
 * platform (admin) does not carry layout's.
 */
export const NO_PLATFORM: AppPlatform = {
  app: "layout",
  useUser: () => null,
  useScope: () => NO_SCOPE,
  useFeatureFlags: () => NO_FLAGS,
  useNotifier: () => SILENT,
  links: { stakeholder: () => null },
  capabilities: {},
};

/**
 * What a render with no `AppContextProvider` above it reads: `NO_PLATFORM`'s
 * behaviour under its own identity, so a missing provider can be told apart
 * from one that gives `NO_PLATFORM` on purpose (a promise modal's own root, a
 * test that means "no app").
 */
const MISSING_PLATFORM: AppPlatform = { ...NO_PLATFORM };

let hasWarnedOfMissingPlatform = false;

/**
 * Said once, in development and tests, when shared code reads the app's
 * platform with no provider above: it then acts with no scope, no flags and
 * a silent notifier, which hides a missing provider rather than failing.
 */
const warnOfMissingPlatform = (hook: string) => {
  if (hasWarnedOfMissingPlatform || import.meta.env.MODE === "production") {
    return;
  }
  hasWarnedOfMissingPlatform = true;
  // eslint-disable-next-line no-console -- a development-only hint, said once
  console.warn(
    `AppContext: ${hook} rendered with no AppContextProvider above it, so it acts with no scope, no feature flags and a silent notifier. ` +
      "Give the app's platform at its root (AppContextProvider), a test's with AppContextTestProvider, or NO_PLATFORM explicitly where no app is meant. " +
      "The stack below names the component that read it.",
    new Error("AppContext read without a provider").stack
  );
};

/** The app's platform, given once at its root (GEN5-5269 ARCHITECTURE A1, item 4). */
const AppPlatformContext = createContext<AppPlatform>(MISSING_PLATFORM);

/** The platform, saying once when it is read with no provider above. */
const usePlatform = (hook: string): AppPlatform => {
  const platform = useContext(AppPlatformContext);
  if (platform === MISSING_PLATFORM) {
    warnOfMissingPlatform(hook);
  }
  return platform;
};

/** A scope a subtree sets over the app's own (an admin page that acts on one organization). */
const AppScopeOverride = createContext<AppScope | null>(null);

export function AppContextProvider({
  platform,
  children,
}: {
  platform: AppPlatform;
  children: ReactNode;
}) {
  return (
    <AppPlatformContext.Provider value={platform}>
      {children}
    </AppPlatformContext.Provider>
  );
}

/** Sets the scope for a subtree, e.g. `{ kind: "admin", organizationId }` on an organization's page. */
export function AppScopeProvider({
  scope,
  children,
}: {
  scope: AppScope;
  children: ReactNode;
}) {
  return (
    <AppScopeOverride.Provider value={scope}>
      {children}
    </AppScopeOverride.Provider>
  );
}

/** The app's platform itself (its hooks unresolved): for code that builds on it, like the Genie host. */
export const useAppPlatform = (): AppPlatform => usePlatform("useAppPlatform");

/** Where the app acts for this render. */
export const useAppScope = (): AppScope => {
  const override = useContext(AppScopeOverride);
  const scope = usePlatform("useAppScope").useScope();
  return override ?? scope;
};

/**
 * The workspace this render is in. Throws outside one: call it only under a
 * workspace route guard (A1), where a missing workspace is a bug.
 */
export const useWorkspaceScope = (): WorkspaceAppScope => {
  const scope = useAppScope();
  if (scope.kind !== "workspace") {
    throw new Error(
      `useWorkspaceScope: rendered in the ${scope.kind} scope, not a workspace`
    );
  }
  return scope;
};

/** The feature flags of the scope's organization, read the app's way. */
export const useAppFeatureFlags = (): AppFeatureFlags => {
  const scope = useAppScope();
  return usePlatform("useAppFeatureFlags").useFeatureFlags(scope);
};

/** How this app tells the user (its verbosity is the app's: D3, AD1). */
export const useAppNotifier = (): Notifier =>
  usePlatform("useAppNotifier").useNotifier();

/** Everything at once; prefer the narrower hooks where one part is enough. */
export const useAppContext = (): AppContextValue => {
  const platform = usePlatform("useAppContext");
  const user = platform.useUser();
  const scope = useAppScope();
  const featureFlags = platform.useFeatureFlags(scope);
  const notify = platform.useNotifier();
  return useMemo(
    () => ({
      app: platform.app,
      user,
      scope,
      featureFlags,
      notify,
      links: platform.links,
      capabilities: platform.capabilities,
    }),
    [platform, user, scope, featureFlags, notify]
  );
};
